// src/pages/CortesHistorial.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./CortesHistorial.css";
import {
  BarraFiltros, Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";
import Toast from "../components/Toast";
import { authFetch } from "../utils/authFetch";

/* =========================
   Helpers
========================= */
function getNowMY() {
  const d = new Date();
  return { mes: d.getMonth() + 1, anio: d.getFullYear() };
}

function mesNombre(m) {
  const nombres = [
    "Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
  ];
  return nombres[(m || 1) - 1] || "Mes";
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

async function fetchJSON(url, options = {}) {
  const res = await authFetch(url, options);
  const text = await res.text();

  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`HTTP ${res.status} respuesta no-JSON: ${text.slice(0, 300)}`);
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.mensaje)) ? (data.error || data.mensaje) : "Error HTTP";
    throw new Error(`HTTP ${res.status}: ${msg}`);
  }

  return data;
}

function fmtDate(v) {
  if (!v) return "—";
  return String(v);
}

function estadoPill(row) {
  // ✅ Depurado: solo 2 estados visuales
  if (row.fecha_reconexion) {
    return { label: "Reconectado", variante: "activo", icono: Iconos.activo };
  }
  return { label: "Cortado", variante: "cortado", icono: Iconos.cortado };
}

function filtroLabel(f) {
  if (f === "pendientes") return "Pendientes";
  if (f === "reconectados") return "Reconectados";
  return "Todos";
}

/* Las dos tablas no comparten columnas y no tiene sentido forzarlas: un
   corte de fibra se hace en la OLT (puerto y ONT) y uno de WiFi en el
   MikroTik (IP y lista). Solo cambian las tres de en medio. */
const COLUMNAS_PROPIAS = {
  fibra: ["OLT", "Puerto", "ONTID"],
  wifi: ["Router", "IP", "Lista"],
};

export default function CortesHistorial() {
  const API_BASE = "/api";

  const now = getNowMY();
  const [mes, setMes] = useState(now.mes);
  const [anio, setAnio] = useState(now.anio);

  const [filtro, setFiltro] = useState("pendientes");
  const [servicio, setServicio] = useState("fibra");

  // De qué servicio son las filas que hay AHORA en pantalla. No es lo
  // mismo que `servicio`: entre que se cambia el selector y llega la
  // respuesta, la tabla sigue mostrando lo anterior, y las cabeceras
  // tienen que seguir a los datos, no al selector.
  const [servicioRows, setServicioRows] = useState("fibra");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [q, setQ] = useState("");

  const rowsFiltradas = useMemo(() => {
    const query = (q || "").trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((r) => {
      const hay = [
        r.cliente_id,
        r.nombre,
        r.apellidos,
        r.equipo,
        // fibra
        r.port,
        r.olt_id,
        r.onu_id,
        // wifi (los que no vienen quedan vacíos y no estorban)
        r.ip,
        r.router,
        r.lista,
      ]
        .map((x) => (x == null ? "" : String(x).toLowerCase()))
        .join(" ");

      return hay.includes(query);
    });
  }, [rows, q]);

  // `sobre` permite forzar el servicio: al cambiar el selector hay que
  // consultar con el nuevo valor, que en ese momento todavía no está en
  // el estado.
  async function buscar(sobre = {}) {
    const srv = sobre.servicio || servicio;
    try {
      setLoading(true);

      const url =
        `${API_BASE}/cortes/historial?mes=${mes}&anio=${anio}` +
        `&filtro=${encodeURIComponent(filtro)}` +
        `&servicio=${encodeURIComponent(srv)}`;

      const data = await fetchJSON(url);

      if (data.ok) {
        setRows(data.rows || []);
        setServicioRows(data.servicio || srv);
      } else {
        setRows([]);
        setToast({ mensaje: data.error || "Error consultando cortes", tipo: "error" });
      }
    } catch (e) {
      console.log("ERROR cortes historial buscar:", e);
      setRows([]);
      setToast({
        mensaje: e?.message || "Error de red consultando cortes",
        tipo: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  function cambiarServicio(s) {
    if (s === servicio) return;
    setServicio(s);
    setRows([]);
    // Cambiar de servicio es cambiar de tabla entera: se consulta sola,
    // porque dejar la pantalla vacía esperando un clic no ayuda a nadie.
    buscar({ servicio: s });
  }

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = rows.length;
  const totalFiltrado = rowsFiltradas.length;

  return (
    <div className="cortesH-page">
      <EncabezadoPagina
        icono={Iconos.historial}
        titulo="Historial de cortes"
        resumen={`${servicio === "wifi" ? "WiFi" : "Fibra"} · quién fue cortado y quién ya se reconectó (pagó).`}
      />

      <div className="cortesH-hero">
        {/* OPTIONS */}
        <div className="cortesH-toolbar">
          <div className="cortesH-pill">
            <label>Mes</label>
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {Array.from({ length: 12 }).map((_, i) => {
                const m = i + 1;
                return (
                  <option value={m} key={m}>
                    {m} · {mesNombre(m)}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="cortesH-pill">
            <label>Año</label>
            <input
              type="number"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              min="2020"
              max="2100"
            />
          </div>

          <div className="cortesH-pill">
            <label>Servicio</label>
            <select value={servicio} onChange={(e) => cambiarServicio(e.target.value)}>
              <option value="fibra">Fibra</option>
              <option value="wifi">WiFi</option>
            </select>
          </div>

          <div className="cortesH-pill">
            <label>Filtro</label>
            <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
              <option value="pendientes">Pendientes de reconexión</option>
              <option value="reconectados">Reconectados (ya pagaron)</option>
              <option value="todos">Todos</option>
            </select>
          </div>

          <Boton icono={Iconos.buscar} onClick={buscar} type="button">
            Buscar
          </Boton>
        </div>

        {/* ✅ GADGETS (debajo, 1 solo color, a lo ancho) */}
        <div className="cortesH-stats">
          <div className="cortesH-statcard">
            <span className="cortesH-statlabel">Periodo</span>
            <span className="cortesH-statvalue">{mesNombre(mes)} {anio}</span>
          </div>

          <div className="cortesH-statcard">
            <span className="cortesH-statlabel">Servicio</span>
            <span className="cortesH-statvalue">{servicio === "wifi" ? "WiFi" : "Fibra"}</span>
          </div>

          <div className="cortesH-statcard">
            <span className="cortesH-statlabel">Filtro</span>
            <span className="cortesH-statvalue">{filtroLabel(filtro)}</span>
          </div>

          <div className="cortesH-statcard">
            <span className="cortesH-statlabel">Total</span>
            <span className="cortesH-statvalue">{total}</span>
          </div>

          <div className="cortesH-statcard">
            <span className="cortesH-statlabel">Mostrando</span>
            <span className="cortesH-statvalue">{totalFiltrado}</span>
          </div>
        </div>
      </div>

      <BarraFiltros
        valor={q}
        onBuscar={setQ}
        placeholder={
          servicioRows === "wifi"
            ? "Buscar por ID, nombre, IP, router…"
            : "Buscar por ID, nombre, puerto, OLT…"
        }
      />

      <div className="cortesH-card">
        <div className="cortesH-card-header">
          <h2>
            <Iconos.tijeras size={17} strokeWidth={1.8} />
            Cortes {servicioRows === "wifi" ? "WiFi" : "fibra"} · {mesNombre(mes)} {anio}
          </h2>
          <Insignia variante="neutra" texto={`${totalFiltrado} registros`} />
        </div>

        <div className="cortesH-table-wrap">
          <table className="cortesH-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Equipo</th>
                {COLUMNAS_PROPIAS[servicioRows].map((c) => (
                  <th key={c}>{c}</th>
                ))}
                <th>Resultado</th>
                <th>Fecha corte</th>
                <th>Fecha reconexión</th>
                <th>Nota</th>
              </tr>
            </thead>

            <tbody>
              {rowsFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="10" className="cortesH-empty">
                    No hay cortes con ese filtro/periodo
                  </td>
                </tr>
              ) : (
                rowsFiltradas.map((r) => {
                  const pill = estadoPill(r);

                  return (
                    <tr key={`${r.cliente_id}-${r.mes}-${r.anio}-${r.id || ""}`}>
                      <td className="cortesH-id">{r.cliente_id}</td>

                      <td className="cortesH-name">
                        {r.nombre} {r.apellidos}
                      </td>

                      <td>
                        <span className={cn("cortesH-pilltag", r.equipo)}>
                          {r.equipo === "propio"
                            ? "Propio"
                            : r.equipo === "prestado"
                            ? "Prestado"
                            : (r.equipo || "—")}
                        </span>
                      </td>

                      {servicioRows === "wifi" ? (
                        <>
                          <td>{r.router || (r.mikrotik_id ?? "—")}</td>
                          <td>{r.ip || "—"}</td>
                          <td>{r.lista || "—"}</td>
                        </>
                      ) : (
                        <>
                          <td>{r.olt_id ?? "—"}</td>
                          <td>{r.port || "—"}</td>
                          <td>{r.onu_id ?? "—"}</td>
                        </>
                      )}

                      <td>
                        <Insignia variante={pill.variante} icono={pill.icono} texto={pill.label} />
                      </td>

                      <td>{fmtDate(r.fecha_corte)}</td>
                      <td>{fmtDate(r.fecha_reconexion)}</td>

                      {/* En WiFi el mensaje del corte dice algo (por qué falló,
                          o "Pago registrado"); en fibra esa columna no existe. */}
                      <td className="cortesH-note">
                        {r.mensaje || (r.fecha_reconexion ? "Pago registrado" : "—")}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && (
        <div className="cortesH-loading-overlay">
          <div className="cortesH-loading-card">
            <div className="cortesH-spinner" />
            <p>Consultando...</p>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          mensaje={toast.mensaje}
          tipo={toast.tipo}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

